import { useHasPermission } from '@/auth/permissions'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { useDebounceCallback } from 'usehooks-ts'
import { useTasksFilter } from './useTasksFilter'

import {
  Button2,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Input2,
  PanelTabsList,
  PanelTabsTrigger,
  Tabs,
  cn,
} from '@valuecase/ui-components'

import { useAuthState } from '@/auth/auth'
import { getCleanSub } from '@valuecase/common'
import { SpaceOwnerFilterDropdown } from '../filtering/SpaceOwnerFilterDropdown'

export const TasksFilter = () => {
  const { view, ownerIds, search, taskStatus } = useTasksFilter()

  const navigate = useNavigate()

  const [searchValue, setSearchValue] = useState(search || '')

  const selectedOwners = useMemo(() => (ownerIds ? new Set(ownerIds) : undefined), [ownerIds])

  const setSearchDebounced = useDebounceCallback(
    (search: string) =>
      navigate({
        to: '/tasks',
        search: (prev) => ({ ...prev, search }),
      }),
    300,
  )

  const hasSpacesAllAdministrationPermission = useHasPermission('SPACES_ALL_ADMINISTRATION')
  const auth = useAuthState()
  const userId = useMemo(() => (auth.authorized ? getCleanSub(auth.sub) : null), [auth])

  const showResetButton = useMemo(() => {
    if (hasSpacesAllAdministrationPermission) {
      const selectedOwnersContainOther = [...(selectedOwners ?? [])].some((id) => id !== userId)
      return (
        searchValue.length > 0 ||
        !selectedOwners ||
        selectedOwnersContainOther ||
        taskStatus !== 'all'
      )
    }

    return searchValue.length > 0
  }, [hasSpacesAllAdministrationPermission, searchValue.length, selectedOwners, taskStatus, userId])

  const handleTaskStatusChange = useCallback(
    (value: 'open' | 'completed' | 'includeCompleted' | 'all') => {
      if (value === 'open') {
        navigate({
          to: '/tasks',
          search: (prev) => {
            const { taskStatus, ...rest } = prev
            // By Default the backend filters out completed tasks, so by removing the taskStatus parameter, we get uncomplete tasks
            return rest
          },
        })
      } else {
        navigate({
          to: '/tasks',
          search: (prev) => ({ ...prev, taskStatus: value }),
        })
      }
    },
    [navigate],
  )

  const taskStatusComputed = useMemo(
    () => (taskStatus === 'completed' || taskStatus === 'includeCompleted' ? taskStatus : 'open'),
    [taskStatus],
  )

  const shouldShowCompletedTasksFilter = useMemo(() => {
    return taskStatus === 'completed' || taskStatus === 'includeCompleted' || taskStatus === 'all'
  }, [taskStatus])

  const taskStatusLabel = useMemo(() => {
    if (taskStatusComputed === 'open') return 'Open tasks'
    if (taskStatusComputed === 'completed') return 'Completed tasks'
    if (taskStatusComputed === 'includeCompleted') return 'All tasks'
    return 'Select task status'
  }, [taskStatusComputed])

  const taskFilters = useMemo(
    () => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <DropdownMenuButton
            className={cn({
              'border-primary-s5 text-primary-s5 font-semibold': shouldShowCompletedTasksFilter,
            })}
            trailingIconClassName={cn({ 'text-primary-s5': shouldShowCompletedTasksFilter })}
          >
            {taskStatusLabel}
          </DropdownMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start'>
          <DropdownMenuRadioGroup
            value={taskStatusComputed}
            onValueChange={(value) =>
              handleTaskStatusChange(value as 'open' | 'completed' | 'includeCompleted')
            }
          >
            <DropdownMenuRadioItem value='open'>Open tasks</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value='completed'>Completed tasks</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value='includeCompleted'>All tasks</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
    [taskStatusComputed, handleTaskStatusChange, shouldShowCompletedTasksFilter, taskStatusLabel],
  )

  const resetFilters = useCallback(() => {
    setSearchValue('')
    navigate({
      to: '/tasks',
      search: (prev) => ({
        ...prev,
        ownerIds: hasSpacesAllAdministrationPermission && userId ? [userId] : undefined,
        search: undefined,
        taskStatus: 'all',
      }),
    })
  }, [hasSpacesAllAdministrationPermission, navigate, userId])

  const ownerFilter = useMemo(
    () => (
      <SpaceOwnerFilterDropdown
        labelForNoSelection='All Space Owners'
        labelForSelection='Space owners'
        searchPlaceholder='Search space owners'
        selectedIds={selectedOwners}
        setSelectedIds={(ids) =>
          navigate({
            to: '/tasks',
            search: (prev) => ({ ...prev, ownerIds: ids ? [...ids] : undefined }),
          })
        }
      />
    ),
    [selectedOwners, navigate],
  )

  return (
    <div className='flex items-center gap-2 w-full justify-between'>
      <div className='flex items-center gap-2'>
        {view === 'list' && (
          <Input2
            leadingIcon='search'
            className='max-w-[200px]'
            placeholder='Search'
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value)
              setSearchDebounced(e.target.value)
            }}
          />
        )}
        {selectedOwners && selectedOwners.size > 0 && ownerFilter}
        {shouldShowCompletedTasksFilter && taskFilters}
      </div>
      <div className='flex items-center gap-2'>
        {showResetButton && (
          <Button2 variant={'plain-subtle'} leadingIcon='refresh' onClick={resetFilters}>
            Reset
          </Button2>
        )}
        {hasSpacesAllAdministrationPermission && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button2 variant={'outlined-white'} leadingIcon='options' className='ml-auto'>
                Filters
              </Button2>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='w-[276px] flex flex-col gap-6 p-4' align='end'>
              {ownerFilter}
              {taskFilters}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <Tabs
          value={view}
          onValueChange={(v) =>
            navigate({
              to: '/tasks',
              search: (prev) => ({ ...prev, view: v as 'columns' | 'list' }),
            })
          }
        >
          <PanelTabsList>
            <PanelTabsTrigger leadingIcon='c_columns' value='columns' />
            <PanelTabsTrigger leadingIcon='list' value='list' />
          </PanelTabsList>
        </Tabs>
      </div>
    </div>
  )
}
